import { HomeServiceDashboardRow } from "generated/openApi";
import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import ApiService from "services/apiService";
import { ServicePeriod } from "../types";

interface ServiceFilter {
    programId: number | null;
}

interface ServiceDataContextValue {
    thisMonthRows: HomeServiceDashboardRow[] | undefined;
    previousMonthRows: HomeServiceDashboardRow[] | undefined;
    loading: boolean;
    currentRows: HomeServiceDashboardRow[] | undefined;
    refetch: () => void;

    selectedPeriod: ServicePeriod;
    updateSelectedPeriod: (period: ServicePeriod) => void;

    filter: ServiceFilter;
    updateFilter: (updates: Partial<ServiceFilter>) => void;

}

const ServiceDataContext = createContext<ServiceDataContextValue | undefined>(undefined);

interface ServiceDataProviderProps {
    children: ReactNode;
}

export const ServiceDataProvider = ({ children }: ServiceDataProviderProps) => {
    const [thisMonthRows, setThisMonthRows] = useState<HomeServiceDashboardRow[] | undefined>(undefined);
    const [previousMonthRows, setPreviousMonthRows] = useState<HomeServiceDashboardRow[] | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedPeriod, setSelectedPeriod] = useState<ServicePeriod>(ServicePeriod.ThisMonth);

    const [filter, setFilter] = useState<ServiceFilter>({
        programId: null,
    });

    const fetchData = useCallback(() => {
        setLoading(true);
        ApiService.home.getServiceDashboard(filter.programId ?? undefined)
            .then((response) => {
                setThisMonthRows(response.thisMonthRows);
                setPreviousMonthRows(response.previousMonthRows);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Failed to fetch service dashboard:", error);
                setLoading(false);
            });
    }, [filter.programId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const updateFilter = useCallback((updates: Partial<ServiceFilter>) => {
        setFilter(prev => ({ ...prev, ...updates }));
    }, []);

    const currentRows = selectedPeriod === ServicePeriod.ThisMonth ? thisMonthRows : previousMonthRows;

    const value: ServiceDataContextValue = {
        thisMonthRows,
        previousMonthRows,
        loading,
        selectedPeriod,
        updateSelectedPeriod: setSelectedPeriod,
        currentRows,
        refetch: fetchData,
        filter,
        updateFilter,
    };

    return (
        <ServiceDataContext.Provider value={value}>
            {children}
        </ServiceDataContext.Provider>
    );
};

export const useServiceData = (): ServiceDataContextValue => {
    const context = useContext(ServiceDataContext);
    if (context === undefined) {
        throw new Error("useServiceData must be used within a ServiceDataProvider");
    }
    return context;
};